import { useCallback, useMemo } from "react";
import { supabase } from "@/lib/supabase";
import { getInitials } from "@/lib/utils";
import { useUser } from "@/hooks/useUser";

export interface UserProfile {
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  avatar_url?: string | null;
}

export const useUserNav = () => {
  const { data, isLoading } = useUser();

  const profile = useMemo<UserProfile | null>(() => {
    if (!data) return null;

    const { user, profile: dbProfile } = data;

    return {
      first_name: dbProfile?.first_name || user.user_metadata?.first_name || null,
      last_name: dbProfile?.last_name || user.user_metadata?.last_name || null,
      email: dbProfile?.email || user.email || null,
    };
  }, [data]);

  const { initials, fullName } = useMemo(() => {
    const initialsStr = getInitials(
      profile?.first_name,
      profile?.last_name,
      profile?.email,
    );
    const fullNameStr =
      [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") ||
      "User";
    return { initials: initialsStr, fullName: fullNameStr };
  }, [profile]);

  const handleLogout = useCallback(async () => {
    try {
      await supabase.auth.signOut();
      // SIGNED_OUT → AuthQueryCacheSync clears cache and navigates to /login
    } catch (error) {
      console.error("Error signing out:", error);
    }
  }, []);

  return useMemo(
    () => ({
      profile,
      initials,
      fullName,
      isLoading,
      handleLogout,
    }),
    [profile, initials, fullName, isLoading, handleLogout],
  );
};